import { useEffect, useState } from 'react';
import { Save, Plus, Trash2, ExternalLink, Users, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Card, CardContent, Input, useConfirm } from '@nepal-ecom/ui';
import { PageHeader } from '@/components/shared/PageHeader';
import { FileImageUploader } from '@/components/shared/FileImageUploader';
import { STOREFRONT_URL, cmsUrl, cmsFetch } from '@/lib/cms';

const CMS_URL = cmsUrl('team');

interface TeamMember {
  name: string;
  role: string;
  avatar: string;
}

const EMPTY_MEMBER: TeamMember = { name: '', role: '', avatar: '' };

// One member = one rounded card. Image on the left, two fields stacked on the
// right, single remove button. No tiny mismatched preview alongside the big
// uploader — the uploader itself shows the chosen image.
function MemberCard({
  member, index, onChange, onDelete,
}: {
  member: TeamMember;
  index: number;
  onChange: (i: number, m: TeamMember) => void;
  onDelete: (i: number) => void;
}) {
  const set = (field: keyof TeamMember, val: string) =>
    onChange(index, { ...member, [field]: val });

  return (
    <Card>
      <CardContent className="p-3">
        <div className="flex items-center gap-3">
          {/* Compact avatar uploader (80px square) */}
          <div className="w-20 flex-shrink-0">
            <FileImageUploader
              label="Photo"
              value={member.avatar}
              onChange={(url) => set('avatar', url)}
              aspect="aspect-square"
            />
          </div>

          {/* Fields — side-by-side on >=sm, stacked on mobile */}
          <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Name</span>
              <Input
                value={member.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="Anita Sharma"
                className="mt-0.5 h-9"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Role / Title</span>
              <Input
                value={member.role}
                onChange={(e) => set('role', e.target.value)}
                placeholder="Founder & CEO"
                className="mt-0.5 h-9"
              />
            </label>
          </div>

          <button
            type="button"
            onClick={() => onDelete(index)}
            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
            aria-label="Remove member"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

export function TeamCms() {
  const confirm = useConfirm();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await cmsFetch(CMS_URL, { cache: 'no-store' });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) setMembers(data as TeamMember[]);
        }
      } catch {
        toast.error('Could not reach storefront CMS.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleChange = (i: number, m: TeamMember) =>
    setMembers((arr) => arr.map((x, idx) => (idx === i ? m : x)));

  const handleDelete = async (i: number) => {
    if (!(await confirm({
      title: 'Remove this team member?',
      confirmLabel: 'Remove',
      tone: 'danger',
    }))) return;
    setMembers((arr) => arr.filter((_, idx) => idx !== i));
  };

  const handleAdd = () => setMembers((arr) => [...arr, { ...EMPTY_MEMBER }]);

  const handleSave = async () => {
    const invalid = members.some((m) => !m.name.trim() || !m.role.trim());
    if (invalid) {
      toast.error('All members need a name and role.');
      return;
    }
    setSaving(true);
    try {
      const res = await cmsFetch(CMS_URL, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(members),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Team saved', { description: 'Live on the storefront in a moment.' });
    } catch (err) {
      toast.error('Save failed', { description: (err as Error).message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="animate-fade-in space-y-5 max-w-3xl">
      <PageHeader
        title="Team Members"
        description="People shown in the 'Built by Nepalis' section on the homepage."
        actions={
          <div className="flex items-center gap-2">
            {STOREFRONT_URL && (
              <Button asChild variant="outline" size="sm">
                <a href={STOREFRONT_URL} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" /> Open storefront
                </a>
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving || loading}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        }
      />

      {loading ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
            Loading team…
          </CardContent>
        </Card>
      ) : members.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-700">No team members yet</p>
            <p className="text-xs text-slate-500 mt-1 mb-4">
              Add the people you want shown on the homepage.
            </p>
            <Button size="sm" onClick={handleAdd}>
              <Plus className="h-4 w-4" /> Add first member
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {members.map((m, i) => (
            <MemberCard
              key={i}
              member={m}
              index={i}
              onChange={handleChange}
              onDelete={handleDelete}
            />
          ))}
          <Button variant="outline" size="sm" className="w-full" onClick={handleAdd}>
            <Plus className="h-4 w-4" /> Add another member
          </Button>
        </div>
      )}

      <p className="text-[11px] text-slate-400 leading-relaxed">
        Tip: the order here determines the order on the storefront's orbit animation
        on desktop and the 2-column grid on mobile.
      </p>
    </div>
  );
}
